import React from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Link,
} from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { AllowPayoutAmountEditingToggle } from 'components/AllowPayoutAmountEditingToggle';
import { handleDecimalPlaces } from 'utils';
import { RegionFormValues } from '../containers/useRegionContainer';

interface CostStructureFieldsProps {
  control: Control<RegionFormValues>;
  phaseStructureType: string;
  errors?: FieldErrors<RegionFormValues>;
  disabled?: boolean;
  isEditMode?: boolean;
  regionId?: string;
}

interface NumberFieldProps {
  name: keyof RegionFormValues;
  control: Control<RegionFormValues>;
  label: string;
  min?: number;
  max?: number;
  step?: number;
  error?: string;
  className?: string;
  disabled?: boolean;
  deps?: keyof RegionFormValues;
}

const NumberField: React.FC<NumberFieldProps> = ({
  name,
  control,
  label,
  min = 0,
  max = 100,
  step = 0.01,
  error,
  className,
  disabled,
  deps,
}) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={{ deps }}
      render={({ field: { onChange, onBlur, value, ref } }) => (
        <TextField
          id={`${name}-input`}
          className={className}
          label={label}
          type="number"
          value={value ?? ''}
          onChange={e => {
            const parsed = handleDecimalPlaces(e.target.value);
            onChange(parsed === '' ? null : Number(parsed));
          }}
          onBlur={onBlur}
          inputRef={ref}
          inputProps={{ min, max, step }}
          error={!!error}
          helperText={error}
          margin="dense"
          variant="filled"
          disabled={disabled}
        />
      )}
    />
  );
};

export const CostStructureFields: React.FC<CostStructureFieldsProps> = ({
  control,
  phaseStructureType,
  errors = {},
  disabled = false,
  isEditMode = false,
  regionId,
}) => {
  const classes = useStyles();

  return (
    <Box
      width="100%"
      display="flex"
      flexDirection="column"
      mx={2}
      mt={3}
      borderTop="lightgray 1px solid"
      pt={2}
    >
      <Box display="flex" alignItems="center">
        <Typography variant="body1" gutterBottom className={classes.bold}>
          Default Region - Cost Structure
        </Typography>
        {isEditMode && regionId && (
          <Link
            component={RouterLink}
            to={`/region-audit?filter=${encodeURIComponent(
              JSON.stringify({ regionId }),
            )}`}
            className={classes.changelogLink}
          >
            View Changelog
          </Link>
        )}
      </Box>

      <Box mt={2} mb={2}>
        <FormControl component="fieldset">
          <Typography variant="caption" className={classes.labelCaption}>
            Phase structure type
          </Typography>
          <Controller
            name="phaseStructureType"
            control={control}
            render={({ field }) => (
              <RadioGroup row {...field}>
                <FormControlLabel
                  value="Drywall"
                  control={<Radio color="primary" size="small" />}
                  label="Drywall"
                  disabled={isEditMode}
                />
                <FormControlLabel
                  value="DrywallAndPaint"
                  control={<Radio color="primary" size="small" />}
                  label="Drywall and Paint"
                  disabled={isEditMode}
                />
              </RadioGroup>
            )}
          />
        </FormControl>
      </Box>

      {phaseStructureType === 'Drywall' && (
        <>
          <Box mt={2}>
            <Box display="flex" mb={2}>
              <Typography variant="body2" gutterBottom className={classes.bold}>
                Production Cost Structure
              </Typography>
            </Box>
            <Box display="flex">
              <NumberField
                name="dwHangingCostPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Hang (Per Board)"
                error={errors.dwHangingCostPerBoard?.message}
                disabled={disabled}
              />
              <NumberField
                name="dwFinishingCostPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Finish (Per Board)"
                error={errors.dwFinishingCostPerBoard?.message}
                disabled={disabled}
              />
              <NumberField
                name="dwSprayingCostPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Spray (Per Board)"
                error={errors.dwSprayingCostPerBoard?.message}
                disabled={disabled}
              />
            </Box>
          </Box>
          <Box my={4}>
            <Box display="flex" mb={2}>
              <Typography variant="body2" gutterBottom className={classes.bold}>
                Warranty / Repair Cost Structure
              </Typography>
            </Box>
            <Box display="flex">
              <NumberField
                name="repairsRate"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Repair Rate (Per Hour)"
                error={errors.repairsRate?.message}
                disabled={disabled}
              />
              <NumberField
                name="pieceworkPercentage"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                max={100}
                step={0.01}
                label="Piecework (Percentage)"
                error={errors.pieceworkPercentage?.message}
                disabled={disabled}
              />
              <NumberField
                name="specialJobsPercentage"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                max={100}
                step={0.01}
                label="Special Jobs (Percentage)"
                error={errors.specialJobsPercentage?.message}
                disabled={disabled}
              />
            </Box>
            <AllowPayoutAmountEditingToggle
              control={control}
              name="allowPayoutAmountEditing"
              disabled={disabled}
            />
          </Box>
          <Box mt={1}>
            <Box display="flex" mb={2}>
              <Typography variant="body2" gutterBottom className={classes.bold}>
                Additional Payment Cost Structure
              </Typography>
            </Box>
            <Box display="flex">
              <NumberField
                name="garageCostFixed"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Garage (Fixed Cost)"
                error={errors.garageCostFixed?.message}
                disabled={disabled}
              />
              <NumberField
                name="highCeilingPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                step={0.01}
                label="High Ceiling (Per Board)"
                error={errors.highCeilingPerBoard?.message}
                disabled={disabled}
              />
              <NumberField
                name="screwInspectionPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                step={0.01}
                label="Screw Inspection (Per Board)"
                error={errors.screwInspectionPerBoard?.message}
                disabled={disabled}
              />
            </Box>
          </Box>
        </>
      )}

      {phaseStructureType === 'DrywallAndPaint' && (
        <>
          <Box mt={2}>
            <Box display="flex" mb={2}>
              <Typography variant="body2" gutterBottom className={classes.bold}>
                Production Cost Structure
              </Typography>
            </Box>
            <Box display="flex" mb={2}>
              <NumberField
                name="dwpHangingCostPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Hang (Per Board)"
                error={errors.dwpHangingCostPerBoard?.message}
                disabled={disabled}
              />
              <NumberField
                name="dwpFinishCostPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Finish (Per Board)"
                error={errors.dwpFinishCostPerBoard?.message}
                disabled={disabled}
              />
              <NumberField
                name="dwpPrimeCostPerSqft"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Prime (Per Sq Ft)"
                error={errors.dwpPrimeCostPerSqft?.message}
                disabled={disabled}
              />
              <NumberField
                name="dwpTrim1CostPerSqft"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Trim 1 (Per Sq Ft)"
                error={errors.dwpTrim1CostPerSqft?.message}
                disabled={disabled}
              />
              <NumberField
                name="dwpBump1CostPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Drywall Touch Up 1 (Per Board)"
                error={errors.dwpBump1CostPerBoard?.message}
                disabled={disabled}
              />
            </Box>
            <Box display="flex">
              <NumberField
                name="dwpInteriorPaintCostPerSqft"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Interior Paint (Per Sq Ft)"
                error={errors.dwpInteriorPaintCostPerSqft?.message}
                disabled={disabled}
              />
              <NumberField
                name="dwpBump2CostPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Drywall Touch Up 2 (Per Board)"
                error={errors.dwpBump2CostPerBoard?.message}
                disabled={disabled}
              />
              <NumberField
                name="dwpTrim2CostPerSqft"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Trim 2 (Per Sq Ft)"
                error={errors.dwpTrim2CostPerSqft?.message}
                disabled={disabled}
              />
              <NumberField
                name="dwpFinalPaintCostPerSqft"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Final Paint (Per Sq Ft)"
                error={errors.dwpFinalPaintCostPerSqft?.message}
                disabled={disabled}
              />
              <NumberField
                name="dwpBump3CostPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Drywall Touch Up 3 (Per Board)"
                error={errors.dwpBump3CostPerBoard?.message}
                disabled={disabled}
              />
            </Box>
            <Box mt={2} display="flex">
              <NumberField
                name="dwpCMBluetapeCostPerSqft"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="CM Bluetape (Per Sq Ft)"
                error={errors.dwpCMBluetapeCostPerSqft?.message}
                disabled={disabled}
              />
              <NumberField
                name="dwpFinalBluetapeCostPerSqft"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Final Bluetape (Per Sq Ft)"
                error={errors.dwpFinalBluetapeCostPerSqft?.message}
                disabled={disabled}
              />
            </Box>
            <Box mt={2} display="flex">
              <NumberField
                name="exteriorOnePercentage"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                max={100}
                step={0.01}
                label="Exterior One (Percentage)"
                error={errors.exteriorOnePercentage?.message}
                disabled={disabled}
              />
              <NumberField
                name="exteriorTwoPercentage"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                max={100}
                step={0.01}
                label="Exterior Two (Percentage)"
                error={errors.exteriorTwoPercentage?.message}
                disabled={disabled}
                deps="exteriorOnePercentage"
              />
            </Box>
          </Box>
          <Box mt={4} mb={3}>
            <Box display="flex" mb={2}>
              <Typography variant="body2" gutterBottom className={classes.bold}>
                Warranty / Repair Cost Structure
              </Typography>
            </Box>
            <Box display="flex">
              <NumberField
                name="repairsRate"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Repair Rate (Per Hour)"
                error={errors.repairsRate?.message}
                disabled={disabled}
              />
              <NumberField
                name="pieceworkPercentage"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                max={100}
                step={0.01}
                label="Piecework (Percentage)"
                error={errors.pieceworkPercentage?.message}
                disabled={disabled}
              />
              <NumberField
                name="specialJobsPercentage"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                max={100}
                step={0.01}
                label="Special Jobs (Percentage)"
                error={errors.specialJobsPercentage?.message}
                disabled={disabled}
              />
            </Box>
            <AllowPayoutAmountEditingToggle
              control={control}
              name="allowPayoutAmountEditing"
              disabled={disabled}
            />
          </Box>
          <Box mt={1}>
            <Box display="flex" mb={2}>
              <Typography variant="body2" gutterBottom className={classes.bold}>
                Additional Payment Cost Structure
              </Typography>
            </Box>
            <Box display="flex">
              <NumberField
                name="garageCostFixed"
                control={control}
                className={classes.inputNumberCost}
                min={1}
                max={100}
                step={0.01}
                label="Garage (Fixed Cost)"
                error={errors.garageCostFixed?.message}
                disabled={disabled}
              />
              <NumberField
                name="highCeilingPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                step={0.01}
                label="High Ceiling (Per Board)"
                error={errors.highCeilingPerBoard?.message}
                disabled={disabled}
              />
              <NumberField
                name="screwInspectionPerBoard"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                step={0.01}
                label="Screw Inspection (Per Board)"
                error={errors.screwInspectionPerBoard?.message}
                disabled={disabled}
              />
              <NumberField
                name="interiorOptionsPercentage"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                max={100}
                step={0.01}
                label="Paint Int. (Percentage)"
                error={errors.interiorOptionsPercentage?.message}
                disabled={disabled}
              />
              <NumberField
                name="exteriorOptionsPercentage"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                max={100}
                step={0.01}
                label="Paint Ext. (Percentage)"
                error={errors.exteriorOptionsPercentage?.message}
                disabled={disabled}
              />
            </Box>
            <Box mt={2} display="flex">
              <NumberField
                name="trimOptionPercentage"
                control={control}
                className={classes.inputNumberCost}
                min={0}
                max={100}
                step={0.01}
                label="Trim Option (Percentage)"
                error={errors.trimOptionPercentage?.message}
                disabled={disabled}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

const useStyles = makeStyles({
  bold: {
    fontWeight: 'bold',
  },
  inputNumberCost: {
    margin: '0px 15px 0px 0px',
    minWidth: '240px',
    maxWidth: '240px',
  },
  labelCaption: {
    color: 'rgba(0, 0, 0, 0.54)',
  },
  changelogLink: {
    marginLeft: 16,
    marginBottom: 4,
    fontSize: 14,
    cursor: 'pointer',
  },
});
